import * as React from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Wrapper from "../../../components/TenantWrapper";
import { useState } from "react";
import title from "../../../components/title";
import TextField from "@mui/material/TextField";
import "./CurrentBill.css";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { GetTenantCurrentBill } from "../../../redux/reducer/GetTenantCurrentBill";
import TransitionsModal from "./Modal";
import { useFormik } from "formik";
import dayjs from "dayjs";
import {
  Grid,
  Modal,
  Typography,
  Chip,
  Fade,
  Divider,
  Stack,
  Avatar,
  Container,
  Alert,
  AlertTitle,
  CircularProgress,
  Paper,
  IconButton,
  InputAdornment,
  Backdrop,
} from "@mui/material";
import { ElectricBill } from "../../../redux/reducer/ElectricReading";
import { SsgcBill } from "../../../redux/reducer/SsgcReading";
import { WaterReadings } from "../../../redux/reducer/WaterReading";
import { TrashReadings } from "../../../redux/reducer/TrashReading";
import { MaintananceReadings } from "../../../redux/reducer/MaintananceReading";
import { useParams, useNavigate } from "react-router-dom";
import PaymentIcon from "@mui/icons-material/Payment";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import MessageIcon from "@mui/icons-material/Message";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockIcon from "@mui/icons-material/Lock";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import TagIcon from "@mui/icons-material/Tag";
import VerifiedIcon from "@mui/icons-material/Verified";
import { BASE_URL } from "../../../config/config";
import * as Yup from "yup";
import {
  BILL_TYPES,
  BILL_TYPE_META,
  normalizeBillType,
  BACKEND_KEY,
} from "../../../constants/billTypes";
import { getDueBillTypesForThisCycle } from "../../../utils/billVisibility";
import MonthlyBillSummary from "./MonthlyBillSummary";
import PreviousUnpaidBills from "./PreviousUnpaidBills";
import { useOnlineBillPayment } from "./useOnlineBillPayment";

// The cycle payload is keyed by the backend's spellings; this screen works in
// canonical ones. Inverting BACKEND_KEY keeps that mapping in a single place.
const CANONICAL_FOR_BACKEND_KEY = Object.fromEntries(
  Object.entries(BACKEND_KEY).map(([canonical, backend]) => [backend, canonical])
);

// ── Constants ─────────────────────────────────────────────────────────────────
// NOTE: these `type` values are this screen's own long-standing local
// identifiers (kelectric/ssgc/maintainance) — kept exactly as-is on purpose
// since they're compared against in many places throughout this large file
// (pay-modal dispatch, consumer-number length, etc.) and changing them here
// would risk silently breaking those. Only the *display meta* (icon/color/
// label) is now sourced from the shared canonical module via
// normalizeBillType, so labels/colors stay consistent app-wide.
// Security is intentionally NOT added as an interactive card here — its
// tenant-facing pay/verify flow would need new cases wired into the legacy
// Modal.js dialog this screen reuses, which is out of scope for this pass.
// It still appears read-only in the new MonthlyBillSummary rollup below.
const localMeta = (localType) => {
  const meta = BILL_TYPE_META[normalizeBillType(localType)] || {};
  return { title: meta.title, company: meta.company, icon: meta.icon, color: meta.color, bgColor: meta.bgColor, lightColor: meta.lightColor };
};
const BILL_CARDS = [
  { name: "K-ELECTRIC", type: "kelectric", ...localMeta("kelectric") },
  { name: "SSGC", type: "ssgc", ...localMeta("ssgc") },
  { name: "WATER", type: "water", ...localMeta("water") },
  { name: "MAINTAINANCE", type: "maintainance", ...localMeta("maintainance") },
  { name: "TRASH CHARGES", type: "trash", ...localMeta("trash") },
];

// Auto-fill only for electric & gas (via consumer number)
const AUTO_FILL_TYPES = ["kelectric", "ssgc"];

// ── Validation Schemas ────────────────────────────────────────────────────────

// Schema for tenant uploading their own bill (isTenantUploader === true)
const tenantSelfUploadSchema = Yup.object().shape({
  total_amount: Yup.number()
    .transform((value, originalValue) => {
      if (originalValue === "" || originalValue === null || originalValue === undefined) return undefined;
      const sanitized = String(originalValue).replace(/[, ]+/g, "");
      return isNaN(Number(sanitized)) ? NaN : Number(sanitized);
    })
    .typeError("Total amount must be a number")
    .required("Total amount is required")
    .min(0, "Total amount cannot be negative"),
  message: Yup.string().optional(),
  bill_image: Yup.mixed()
    .required("Bill image or PDF is required")
    .test("fileSize", "File must be less than 5MB", (v) => !v || v.size <= 5 * 1024 * 1024)
    .test("fileType", "Only JPG, PNG or PDF allowed", (v) =>
      !v || ["image/jpeg", "image/png", "image/jpg", "image/webp", "application/pdf"].includes(v.type)
    ),
});

// Schema for tenant verifying landlord's bill (they upload their own image copy)
// Amount is read-only (taken from landlord's bill), only image required
const tenantVerifySchema = Yup.object().shape({
  bill_image: Yup.mixed()
    .required("Please upload your bill image to verify")
    .test("fileSize", "File must be less than 5MB", (v) => !v || v.size <= 5 * 1024 * 1024)
    .test("fileType", "Only JPG, PNG or PDF allowed", (v) =>
      !v || ["image/jpeg", "image/png", "image/jpg", "image/webp", "application/pdf"].includes(v.type)
    ),
});

// Schema for tenant self-upload WITH auto-fill (fields may be locked)
const tenantAutoFillSchema = Yup.object().shape({
  total_amount: Yup.number()
    .transform((value, originalValue) => {
      if (originalValue === "" || originalValue === null || originalValue === undefined) return undefined;
      const sanitized = String(originalValue).replace(/[, ]+/g, "");
      return isNaN(Number(sanitized)) ? NaN : Number(sanitized);
    })
    .typeError("Total amount must be a number")
    .required("Total amount is required")
    .min(0, "Total amount cannot be negative"),
  message: Yup.string().optional(),
  bill_image: Yup.mixed()
    .required("Bill image or PDF is required")
    .test("fileSize", "File must be less than 5MB", (v) => !v || v.size <= 5 * 1024 * 1024)
    .test("fileType", "Only JPG, PNG or PDF allowed", (v) =>
      !v || ["image/jpeg", "image/png", "image/jpg", "image/webp", "application/pdf"].includes(v.type)
    ),
});

// ── Helpers ───────────────────────────────────────────────────────────────────
// `type` may be this screen's local spelling (kelectric/ssgc/maintainance) or
// a canonical BILL_TYPES key (from MonthlyBillSummary) — normalize first,
// then BACKEND_KEY translates it to whatever field name the
// GetTenantCurrentBill response actually uses (e.g. gas -> "ssgc").
function ownerHasUploaded(billData, type) {
  const key = BACKEND_KEY[normalizeBillType(type)];
  if (!key) return false;
  return billData?.[key] === true && billData?.[`${key}_data`]?.[0] != null;
}

function getBillDataByType(billData, type) {
  const key = BACKEND_KEY[normalizeBillType(type)];
  if (!key) return null;
  return billData?.[key] === true ? billData?.[`${key}_data`]?.[0] ?? null : null;
}

const isPdfFile = (file) => file && file.type === "application/pdf";

// ── PDF Preview Component ─────────────────────────────────────────────────────
function PdfPreview({ url, color, onDownload, onOpenNewTab }) {
  return (
    <Box sx={{ borderRadius: 3, overflow: "hidden", border: `2px solid ${color}`, mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.2, backgroundColor: color }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PictureAsPdfIcon sx={{ color: "#fff", fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600, fontSize: "0.85rem" }}>PDF Preview</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {onDownload && (
            <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: 15 }} />} onClick={onDownload}
              sx={{ backgroundColor: "rgba(255,255,255,0.22)", color: "#fff", fontWeight: 700, fontSize: "0.72rem", textTransform: "none", borderRadius: 2, px: 1.5, py: 0.4, border: "1px solid rgba(255,255,255,0.35)", "&:hover": { backgroundColor: "rgba(255,255,255,0.35)" }, minWidth: 0 }}>
              Download
            </Button>
          )}
          {onOpenNewTab && (
            <Button size="small" startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />} onClick={onOpenNewTab}
              sx={{ backgroundColor: "rgba(255,255,255,0.22)", color: "#fff", fontWeight: 700, fontSize: "0.72rem", textTransform: "none", borderRadius: 2, px: 1.5, py: 0.4, border: "1px solid rgba(255,255,255,0.35)", "&:hover": { backgroundColor: "rgba(255,255,255,0.35)" }, minWidth: 0 }}>
              Open
            </Button>
          )}
        </Stack>
      </Box>
      <Box component="iframe" src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} title="PDF Preview"
        sx={{ width: "100%", height: 380, border: "none", display: "block", backgroundColor: "#fff", m: 0, p: 0 }} />
    </Box>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function CurrentBill() {
  title("Current Bill");

  const { landlordId, flatId } = useParams();
  const navigate = useNavigate();
  const { startPayment, loadingId: payingId, dialog: onlinePayDialog } = useOnlineBillPayment();
  const open = useSelector((state) => state.sidebar.open);
  const bill_uploaded_by = useSelector(
    (state) => state?.validateUser?.UserValidate?.data?.tenantProfile?.flatInterestRequests
  );

  const [billData,          setBillData]          = useState(null);
  const [cycle,             setCycle]             = useState(null);
  // Unpaid bills from earlier cycles (server: previousUnpaid).
  const [previousUnpaid,    setPreviousUnpaid]    = useState([]);
  const [previousTotal,     setPreviousTotal]     = useState(0);
  const [selectedBillData,  setSelectedBillData]  = useState(null);
  const [selectedBillType,  setSelectedBillType]  = useState(null);
  const dispatch = useDispatch();

  // pay modal states (original — unchanged)
  const [isModalVisible,    setIsModalVisible]    = useState(false);
  const [ssgcModal,         setSsgcModal]         = useState(false);
  const [waterModal,        setWaterModal]        = useState(false);
  const [maintainanceModal, setMaintainanceModal] = useState(false);
  const [trashModal,        setTrashModal]        = useState(false);

  const getId  = window.location.pathname.replace("/current-Bill/", "");
  const flatID = getId.split("/")[1];

  // ── Modal state ──────────────────────────────────────────────────────────────
  // modalOpen: null | "upload" | "verify"
  // "upload" = tenant self-uploads (isTenantUploader===true)
  //            OR tenant submits via consumer number when owner forgot
  // "verify" = tenant uploads their copy to verify owner's uploaded bill
  const [modalOpen,    setModalOpen]    = React.useState(null);
  const [activeType,   setActiveType]   = React.useState(null);
  const [imagePreview, setImagePreview] = React.useState("");
  const [previewFile,  setPreviewFile]  = React.useState(null);
  const [expandedCard, setExpandedCard] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isFetching,   setIsFetching]   = React.useState(true);

  // ── Auto-fill state (for tenant self-upload modal) ───────────────────────────
  const [consumerNumber,      setConsumerNumber]      = React.useState("");
  const [consumerNumberError, setConsumerNumberError] = React.useState("");
  const [autoFillLoading,     setAutoFillLoading]     = React.useState(false);
  // When bill is auto-fetched, lock the amount field
  const [billFetched,         setBillFetched]         = React.useState(false);
  const [fetchedBillAmount,   setFetchedBillAmount]   = React.useState(null);

  const matchedFlat      = bill_uploaded_by?.find((item) => item?.flatDetail?._id === flatID);
  const uploadedByValue  = matchedFlat?.flatDetail?.bill_uploaded_by;
  // isTenantUploader = true  → tenant uploads bills (owner views)
  // isTenantUploader = false → owner uploads bills (tenant views/pays)
  const isTenantUploader = uploadedByValue === "tenant";

  const activeCard    = BILL_CARDS.find((c) => c.type === activeType);
  const showAutoFill  = AUTO_FILL_TYPES.includes(activeType);
  // Consumer number length: gas = 10, electric = 13
  const consumerNumLength = activeType === "ssgc" ? 10 : 13;

  // ── Fetch bills ─────────────────────────────────────────────────────────────
  const fetchBills = React.useCallback(() => {
    setIsFetching(true);
    dispatch(GetTenantCurrentBill({ id: getId }))
      .then((res) => {
        setBillData(res?.payload?.data?.data);
        // Which month is actually being billed, and whether this tenancy has
        // reached its first cycle at all. Without it a tenant who moved in
        // mid-month saw an empty "No Bills Yet" box and read it as a fault.
        setCycle(res?.payload?.data?.cycle || null);
        setPreviousUnpaid(res?.payload?.data?.previousUnpaid || []);
        setPreviousTotal(res?.payload?.data?.previousOutstanding || 0);
      })
      .catch(() => {})
      .finally(() => setIsFetching(false));
  }, [dispatch, getId]);

  React.useEffect(() => { fetchBills(); }, [fetchBills]);

  // ── Pay modal handlers (original — unchanged) ─────────────────────────────
  const showKElectricModalModal = () => setIsModalVisible(true);
  const showSsgcModal           = () => setSsgcModal(true);
  const showWaterModal          = () => setWaterModal(true);
  const showMaintainanceModal   = () => setMaintainanceModal(true);
  const showTrashModal          = () => setTrashModal(true);

  const handlePayClick = (type, data) => {
    setSelectedBillType(type);
    setSelectedBillData(data);
    switch (type) {
      case "kelectric":    showKElectricModalModal(); break;
      case "ssgc":         showSsgcModal();           break;
      case "water":        showWaterModal();          break;
      case "maintainance": showMaintainanceModal();   break;
      case "trash":        showTrashModal();          break;
      default: break;
    }
  };

  // ── Reset auto-fill state ────────────────────────────────────────────────────
  const resetAutoFill = () => {
    setConsumerNumber("");
    setConsumerNumberError("");
    setBillFetched(false);
    setFetchedBillAmount(null);
  };

  // ── Open upload modal ────────────────────────────────────────────────────────
  // Used when:
  // 1. isTenantUploader === true  → tenant uploads their own bill
  // 2. isTenantUploader === false → owner forgot, tenant self-submits via consumer number
  const openUploadModal = (type) => {
    setActiveType(type);
    setModalOpen("upload");
    setImagePreview("");
    setPreviewFile(null);
    resetAutoFill();
    uploadFormik.resetForm();
  };

  // ── Open verify modal ────────────────────────────────────────────────────────
  // Used when isTenantUploader === false AND owner has already uploaded
  // Tenant uploads their physical copy to verify owner's amount
  const openVerifyModal = (type) => {
    setActiveType(type);
    setModalOpen("verify");
    setImagePreview("");
    setPreviewFile(null);
    verifyFormik.resetForm();
  };

  // ── Close modal ──────────────────────────────────────────────────────────────
  const handleCloseModal = () => {
    if (isSubmitting) return;
    setModalOpen(null);
    setActiveType(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview("");
    setPreviewFile(null);
    resetAutoFill();
    uploadFormik.resetForm();
    verifyFormik.resetForm();
  };

  // ── File handlers ────────────────────────────────────────────────────────────
  const handleFileSelect = (file, formikInstance, force = false) => {
    if (!file) return;
    // If bill is fetched (auto-fill), only allow force override
    if (billFetched && !force) return;
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    const url = URL.createObjectURL(file);
    setImagePreview(url);
    setPreviewFile(file);
    formikInstance.setFieldValue("bill_image", file);
  };

  const handleFileRemove = (formikInstance) => {
    if (billFetched) return; // locked when auto-fill done
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview("");
    setPreviewFile(null);
    formikInstance.setFieldValue("bill_image", null);
    formikInstance.setFieldTouched("bill_image", true, true);
  };

  const handleDownloadPdf = () => {
    if (!imagePreview) return;
    const link = document.createElement("a");
    link.href = imagePreview;
    link.download = (previewFile?.name) || "bill.pdf";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenPdfNewTab = () => {
    if (!imagePreview) return;
    const link = document.createElement("a");
    link.href = imagePreview;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Consumer number change ────────────────────────────────────────────────────
  const handleConsumerNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= consumerNumLength) {
      setConsumerNumber(value);
      setConsumerNumberError(
        value.length > 0 && value.length < consumerNumLength
          ? `Consumer number must be exactly ${consumerNumLength} digits`
          : ""
      );
      // If user changes consumer number after fetch, reset
      if (billFetched) {
        setBillFetched(false);
        setFetchedBillAmount(null);
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImagePreview("");
        setPreviewFile(null);
        uploadFormik.setFieldValue("bill_image", null);
        uploadFormik.setFieldValue("total_amount", "");
      }
    }
  };

  const isAutoFillEnabled = consumerNumber.length === consumerNumLength && !autoFillLoading;

  // ── Auto-fill handler ─────────────────────────────────────────────────────────
  const handleAutoFill = async () => {
    if (!isAutoFillEnabled) return;
    setAutoFillLoading(true);
    try {
      const endpoint = activeType === "ssgc"
        ? `${BASE_URL}/fetch-auto-ssgc-bill`
        : `${BASE_URL}/fetch-auto-ke-bill`;
      const bodyKey = activeType === "ssgc" ? "consumerNumber" : "accountNumber";

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [bodyKey]: consumerNumber }),
      });
      const data = await response.json();

      if (data.success && data.bill) {
        const amount = data.bill.payableAfter;

        // Set amount (locked)
        uploadFormik.setFieldValue("total_amount", amount);
        setFetchedBillAmount(amount);

        // Attach PDF automatically
        const byteCharacters = atob(data.bill.pdfBase64);
        const byteArray = new Uint8Array(Array.from(byteCharacters).map((c) => c.charCodeAt(0)));
        const pdfFile = new File([byteArray], "auto-bill.pdf", { type: "application/pdf" });
        // Force = true to bypass lock check
        handleFileSelect(pdfFile, uploadFormik, true);

        setBillFetched(true);
        toast.success("Bill fetched successfully!", { position: "top-right", autoClose: 3000, theme: "colored" });
      } else {
        toast.error("Could not fetch bill data. Please fill manually.", { position: "top-right", autoClose: 4000, theme: "colored" });
      }
    } catch (err) {
      console.error("Auto-fill error:", err);
      toast.error("Auto-fill failed. Please try again.", { position: "top-right", autoClose: 4000, theme: "colored" });
    } finally {
      setAutoFillLoading(false);
    }
  };

  // ── Upload Formik ─────────────────────────────────────────────────────────────
  // Used for:
  // - isTenantUploader === true: tenant uploads their own bill
  // - isTenantUploader === false: tenant submits bill themselves (owner forgot)
  const uploadFormik = useFormik({
    initialValues: { total_amount: "", message: "", bill_image: "" },
    validationSchema: tenantSelfUploadSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values, action) => {
      const formData = new FormData();
      formData.append("total_amount", values.total_amount);
      formData.append("message",      values.message || "");
      formData.append("upload_date",  dayjs().format("YYYY-MM-DD"));
      formData.append("type",         activeType);
      formData.append("landlordId",   landlordId);
      formData.append("flatId",       flatId);
      if (AUTO_FILL_TYPES.includes(activeType) && consumerNumber) {
        formData.append("consumer_number", consumerNumber);
      }
      if (values.bill_image) formData.append("bill_image", values.bill_image);

      setIsSubmitting(true);
      try {
        const API_MAP = {
          kelectric:    ElectricBill,
          ssgc:         SsgcBill,
          water:        WaterReadings,
          maintainance: MaintananceReadings,
          trash:        TrashReadings,
        };
        await dispatch(API_MAP[activeType]({ formData })).unwrap();
        action.resetForm();
        handleCloseModal();
        fetchBills();
      } catch (err) {
        toast.error(
          err?.message || err?.response?.data?.message || "Something went wrong. Please try again.",
          { position: "top-right", autoClose: 4000, theme: "colored" }
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // ── Verify Formik ─────────────────────────────────────────────────────────────
  // Used when isTenantUploader === false AND owner HAS uploaded
  // Tenant uploads their physical copy to verify the owner's bill
  const verifyFormik = useFormik({
    initialValues: { bill_image: "" },
    validationSchema: tenantVerifySchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values, action) => {
      const formData = new FormData();
      formData.append("type",        activeType);
      formData.append("landlordId",  landlordId);
      formData.append("flatId",      flatId);
      formData.append("upload_date", dayjs().format("YYYY-MM-DD"));
      if (values.bill_image) formData.append("bill_image", values.bill_image);

      setIsSubmitting(true);
      try {
        const API_MAP = {
          kelectric:    ElectricBill,
          ssgc:         SsgcBill,
          water:        WaterReadings,
          maintainance: MaintananceReadings,
          trash:        TrashReadings,
        };
        await dispatch(API_MAP[activeType]({ formData })).unwrap();
        action.resetForm();
        handleCloseModal();
        fetchBills();
      } catch (err) {
        toast.error(
          err?.message || err?.response?.data?.message || "Something went wrong.",
          { position: "top-right", autoClose: 4000, theme: "colored" }
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // ── Visible bills logic ───────────────────────────────────────────────────────
  // isTenantUploader === true  → tenant uploads → show ALL 5 cards so tenant can upload any
  // isTenantUploader === false → owner uploads  → show only cards where owner has uploaded
  const getVisibleBills = () => {
    if (!billData) return [];
    if (isTenantUploader) return BILL_CARDS;
    return BILL_CARDS.filter((card) => ownerHasUploaded(billData, card.type));
  };
  const bills = getVisibleBills();
  // What is payable this cycle comes from the server, which holds the
  // monthly/annual schedule the owner set at registration. Rent and the two
  // metered utilities are always in play; the rest appear only in the cycles
  // their schedule puts them in. The local helper is the pre-response fallback.
  const dueBillTypes = React.useMemo(() => {
    if (!cycle?.charges) return getDueBillTypesForThisCycle(matchedFlat?.flatDetail);

    const scheduled = cycle.charges
      .map((charge) => CANONICAL_FOR_BACKEND_KEY[charge.key])
      .filter(Boolean);

    return [
      ...(cycle.rentAmount ? [BILL_TYPES.RENT] : []),
      BILL_TYPES.ELECTRICITY,
      BILL_TYPES.GAS,
      ...scheduled,
    ];
  }, [cycle, matchedFlat]);

  // ─────────────────────────────────────────────────────────────────────────────
  // BillCard inner component
  // ─────────────────────────────────────────────────────────────────────────────
  function BillCard({ billType, cardMeta }) {
    const isExpanded      = expandedCard === billType;
    const { color, bgColor, lightColor, icon } = cardMeta;
    const currentBillData = getBillDataByType(billData, billType);
    const hasBillData     = currentBillData != null;

    const isBillEnabled = (() => {
      switch (billType) {
        case "kelectric":    return billData?.electricity    === true;
        case "ssgc":         return billData?.ssgc            === true;
        case "water":        return billData?.water           === true;
        case "maintainance": return billData?.maintainance    === true;
        case "trash":        return billData?.trash           === true;
        default: return false;
      }
    })();

    // Pay button logic
    const status     = currentBillData?.approval?.status;
    const isDisabled = status === "approved";
    const getBgColor = () => {
      if (status === "approved") return "#2E7D32";
      if (status === "rejected") return "#D32F2F";
      return `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`;
    };
    const getHoverBg = () => {
      if (status === "approved") return "#1B5E20";
      if (status === "rejected") return "#B71C1C";
      return `linear-gradient(135deg, ${color}dd 0%, ${color} 100%)`;
    };
    const getLabel = () => {
      if (status === "approved") return "Approved";
      if (status === "rejected") return "Rejected - Pay Now";
      return "Pay Bill";
    };

    // Has tenant already verified this bill?
    const tenantAlreadyVerified = currentBillData?.billImages?.tenant?.image != null;

    // ── SCENARIO A: isTenantUploader === true ────────────────────────────────
    // Bill upload is tenant's responsibility
    // → Show "Upload Bill" button always (for all 5 card types)
    // → If tenant has already uploaded, show "View uploaded bills" toggle
    // → Owner has NOT uploaded anything here (tenant is the uploader)
    if (isTenantUploader) {
      return (
        <Card sx={{
          borderRadius: 4, bgcolor: "rgba(255,255,255,0.96)", backdropFilter: "blur(10px)",
          border: `1px solid ${lightColor}`, boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          transition: "box-shadow 0.25s ease", position: "relative", overflow: "hidden",
          "&:hover": { boxShadow: `0 8px 32px ${color}30` },
        }}>
          <Box sx={{ height: 4, background: `linear-gradient(90deg, ${color} 0%, ${color}88 100%)` }} />

          <CardContent sx={{ p: 3, textAlign: "center" }}>
            <Box sx={{
              width: 76, height: 76, borderRadius: "50%", bgcolor: bgColor,
              display: "flex", alignItems: "center", justifyContent: "center",
              mx: "auto", mb: 2, fontSize: "2.2rem",
              boxShadow: `0 4px 16px ${color}35`, border: `3px solid ${lightColor}`,
            }}>
              {icon}
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5 }}>{cardMeta.title}</Typography>
            <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500, mb: 2 }}>{cardMeta.company}</Typography>

            <Divider sx={{ borderColor: lightColor, mb: 2 }} />

            {/* Upload Bill button */}
            <Button
              fullWidth variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => openUploadModal(billType)}
              sx={{
                borderRadius: 3, py: 1.4, textTransform: "none", fontWeight: 700,
                background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
                boxShadow: `0 4px 14px ${color}40`,
                "&:hover": { boxShadow: `0 6px 20px ${color}55`, opacity: 0.92 },
              }}
            >
              Upload Bill
            </Button>

            {/* Show "View uploaded bills" only if tenant has uploaded something */}
            {hasBillData && (
              <Box
                onClick={() => setExpandedCard(isExpanded ? null : billType)}
                sx={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  gap: 0.5, cursor: "pointer", mt: 1.5,
                  color, fontWeight: 600, fontSize: "0.78rem",
                  "&:hover": { opacity: 0.75 },
                }}
              >
                {isExpanded ? "Hide details" : "View uploaded bills"}
                <KeyboardArrowDownIcon sx={{
                  fontSize: 18, transition: "transform 0.3s",
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0)",
                }} />
              </Box>
            )}
          </CardContent>

          {/* Expanded details for tenant-uploaded bills */}
          {hasBillData && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ px: 3, pb: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#e8f5e9", textAlign: "center", border: "2px solid #4caf50" }}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.3}>Bill Amount</Typography>
                      <Typography variant="body1" fontWeight="700" color="success.main">Rs. {currentBillData?.billAmount || "N/A"}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#f8f9fa", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography variant="body2" fontWeight="600" color="text.secondary">Status</Typography>
                      <Chip
                        label={currentBillData?.approval?.status || "Pending"}
                        color={currentBillData?.approval?.status === "approved" ? "success" : "warning"}
                        size="small" sx={{ fontWeight: 700, textTransform: "capitalize" }}
                      />
                    </Box>
                  </Grid>

                  {/* Tenant's uploaded bill file */}
                  {(() => {
                    const raw = currentBillData?.billImages?.tenant;
                    if (!raw) return null;
                    const files = Array.isArray(raw)
                      ? raw
                      : Array.isArray(raw?.images) ? raw.images
                      : raw?.image ? [raw.image]
                      : typeof raw === "string" ? [raw]
                      : [];
                    if (files.length === 0) return null;
                    return (
                      <Grid item xs={12}>
                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#f0fdf4", border: "1px solid #10B981" }}>
                          <Stack direction="row" alignItems="center" spacing={0.5} mb={1.5}>
                            <VerifiedIcon sx={{ color: "#10B981", fontSize: 16 }} />
                            <Typography variant="caption" fontWeight="700" color="#10B981">
                              Your Uploaded Bill{files.length > 1 ? ` (${files.length} files)` : ""}
                            </Typography>
                          </Stack>
                          <Stack spacing={1.5}>
                            {files.map((url, idx) => {
                              const isPdf = typeof url === "string" && (url.toLowerCase().includes(".pdf") || url.toLowerCase().includes("application/pdf"));
                              return (
                                <Box key={idx}>
                                  {isPdf ? (
                                    <Box sx={{ borderRadius: 2, overflow: "hidden", border: "2px solid #10B981" }}>
                                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, py: 1, backgroundColor: "#10B981" }}>
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                          <PictureAsPdfIcon sx={{ color: "#fff", fontSize: 18 }} />
                                          <Typography variant="caption" sx={{ color: "#fff", fontWeight: 700 }}>PDF Bill</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={0.5}>
                                          <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: 13 }} />}
                                            onClick={() => { const a = document.createElement("a"); a.href = url; a.download = `bill-${idx + 1}.pdf`; a.target = "_blank"; a.click(); }}
                                            sx={{ backgroundColor: "rgba(255,255,255,0.22)", color: "#fff", fontWeight: 700, fontSize: "0.68rem", textTransform: "none", borderRadius: 1.5, px: 1, py: 0.3, border: "1px solid rgba(255,255,255,0.35)", "&:hover": { backgroundColor: "rgba(255,255,255,0.35)" }, minWidth: 0 }}>
                                            Download
                                          </Button>
                                          <Button size="small" startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                                            onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                                            sx={{ backgroundColor: "rgba(255,255,255,0.22)", color: "#fff", fontWeight: 700, fontSize: "0.68rem", textTransform: "none", borderRadius: 1.5, px: 1, py: 0.3, border: "1px solid rgba(255,255,255,0.35)", "&:hover": { backgroundColor: "rgba(255,255,255,0.35)" }, minWidth: 0 }}>
                                            Open
                                          </Button>
                                        </Stack>
                                      </Box>
                                      <Box component="iframe" src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} title="Bill PDF"
                                        sx={{ width: "100%", height: 280, border: "none", display: "block", backgroundColor: "#fff" }} />
                                    </Box>
                                  ) : (
                                    <Box>
                                      <Box
                                        component="img" src={url} alt={`Bill ${idx + 1}`}
                                        onClick={() => window.open(url, "_blank")}
                                        sx={{
                                          width: "100%", maxHeight: 240, borderRadius: 2,
                                          objectFit: "contain", cursor: "pointer", display: "block",
                                          bgcolor: "#f0fdf4", border: "2px solid #10B981",
                                          boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
                                          transition: "transform 0.2s", "&:hover": { transform: "scale(1.01)" },
                                        }}
                                      />
                                      <Button size="small" startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                                        onClick={() => window.open(url, "_blank")}
                                        sx={{ fontSize: "0.72rem", textTransform: "none", color: "#10B981", fontWeight: 600, p: 0, mt: 0.5 }}>
                                        View full size
                                      </Button>
                                    </Box>
                                  )}
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>
                      </Grid>
                    );
                  })()}
                </Grid>
              </Box>
            </Collapse>
          )}
        </Card>
      );
    }

    // ── SCENARIO B: isTenantUploader === false ───────────────────────────────
    // Owner uploads bills, tenant views/pays/verifies
    //
    // Sub-cases:
    // B1. Owner HAS uploaded → tenant sees details, pays, and can verify by uploading their copy
    // B2. Owner has NOT uploaded yet → tenant can self-submit via consumer number
    //     (only for electric/gas which support auto-fill)
    return (
      <Card sx={{
        borderRadius: 4, bgcolor: "rgba(255,255,255,0.96)", backdropFilter: "blur(10px)",
        border: `1px solid ${lightColor}`, boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        transition: "box-shadow 0.25s ease", position: "relative", overflow: "hidden",
        "&:hover": { boxShadow: `0 8px 32px ${color}30` },
      }}>
        <Box sx={{ height: 4, background: `linear-gradient(90deg, ${color} 0%, ${color}88 100%)` }} />

        <CardContent sx={{ p: 3, textAlign: "center" }}>
          <Box sx={{
            width: 76, height: 76, borderRadius: "50%", bgcolor: bgColor,
            display: "flex", alignItems: "center", justifyContent: "center",
            mx: "auto", mb: 2, fontSize: "2.2rem",
            boxShadow: `0 4px 16px ${color}35`, border: `3px solid ${lightColor}`,
          }}>
            {icon}
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5 }}>{cardMeta.title}</Typography>
          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500, mb: 2 }}>{cardMeta.company}</Typography>

          <Divider sx={{ borderColor: lightColor, mb: 2 }} />

          {/* B1: Owner has uploaded — show view details + pay + verify */}
          {hasBillData && (
            <>
              {/* View details toggle */}
              <Box
                onClick={() => setExpandedCard(isExpanded ? null : billType)}
                sx={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  gap: 0.5, cursor: "pointer", mb: 1.5,
                  color, fontWeight: 600, fontSize: "0.78rem",
                  "&:hover": { opacity: 0.75 },
                }}
              >
                {isExpanded ? "Hide details" : "View details"}
                <KeyboardArrowDownIcon sx={{
                  fontSize: 18, transition: "transform 0.3s",
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0)",
                }} />
              </Box>

              {/* Pay Bill button */}
              {isBillEnabled && (
                <span style={{ cursor: isDisabled ? "not-allowed" : "pointer", display: "block" }}>
                  <Button
                    fullWidth variant="contained"
                    startIcon={<PaymentIcon />}
                    disabled={isDisabled}
                    onClick={() => handlePayClick(billType, currentBillData)}
                    sx={{
                      borderRadius: 3, py: 1.4, textTransform: "none", fontWeight: 700,
                      background: getBgColor(),
                      boxShadow: "0 4px 15px 0 rgba(0,0,0,0.2)",
                      "&:hover": { background: getHoverBg(), boxShadow: "0 6px 20px 0 rgba(0,0,0,0.3)" },
                      "&.Mui-disabled": { background: getBgColor(), color: "#fff", boxShadow: "none" },
                    }}
                  >
                    {getLabel()}
                  </Button>
                </span>
              )}

              {/* Upload to Verify button — tenant uploads their physical copy */}
              <Button
                fullWidth variant="outlined"
                startIcon={tenantAlreadyVerified ? <VerifiedIcon /> : <CloudUploadIcon />}
                onClick={() => openVerifyModal(billType)}
                disabled={tenantAlreadyVerified}
                sx={{
                  mt: 1.5, borderRadius: 3, py: 1.2, textTransform: "none", fontWeight: 600,
                  borderColor: tenantAlreadyVerified ? "#10B981" : color,
                  color: tenantAlreadyVerified ? "#10B981" : color,
                  borderWidth: 2,
                  "&:hover": { borderWidth: 2, backgroundColor: tenantAlreadyVerified ? "#f0fdf4" : bgColor },
                  "&.Mui-disabled": { borderColor: "#10B981", color: "#10B981", borderWidth: 2 },
                }}
              >
                {tenantAlreadyVerified ? "Bill Verified ✓" : "Upload to Verify"}
              </Button>
            </>
          )}

          {/* B2: Owner has NOT uploaded yet */}
          {!hasBillData && (
            <>
              {/* Info that owner hasn't uploaded */}
              <Box sx={{
                p: 1.5, borderRadius: 2, mb: 2,
                bgcolor: "#fff7ed", border: "1.5px solid #fed7aa",
                display: "flex", alignItems: "center", gap: 0.8,
              }}>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: "#f97316", flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: "#c2410c", fontWeight: 600, fontSize: "0.76rem", textAlign: "left" }}>
                  Landlord hasn't uploaded this bill yet.
                </Typography>
              </Box>

              {/* For electric/gas: tenant can self-submit via consumer number */}
              {AUTO_FILL_TYPES.includes(billType) ? (
                <Button
                  fullWidth variant="outlined"
                  startIcon={<TagIcon />}
                  onClick={() => openUploadModal(billType)}
                  sx={{
                    borderRadius: 3, py: 1.2, textTransform: "none", fontWeight: 600,
                    borderColor: color, color, borderWidth: 2,
                    "&:hover": { borderWidth: 2, backgroundColor: bgColor },
                  }}
                >
                  Submit via Consumer No.
                </Button>
              ) : (
                /* For water/maintenance/trash: no auto-fill, show locked */
                isBillEnabled ? (
                  <Typography variant="caption" sx={{ color: "#94a3b8", display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                    <LockIcon sx={{ fontSize: 14 }} /> Waiting for landlord
                  </Typography>
                ) : (
                  <Typography variant="caption" sx={{ color: "#94a3b8", display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                    <LockIcon sx={{ fontSize: 14 }} /> Not enabled
                  </Typography>
                )
              )}
            </>
          )}
        </CardContent>

        {/* Expanded details (Scenario B1 — owner uploaded) */}
        {hasBillData && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ px: 3, pb: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={1.5}>

                <Grid item xs={6}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#e8f5e9", textAlign: "center", border: "2px solid #4caf50" }}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.3}>Bill Amount</Typography>
                    <Typography variant="body1" fontWeight="700" color="success.main">Rs. {currentBillData?.billAmount || "N/A"}</Typography>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#ffebee", textAlign: "center", border: "2px solid #f44336" }}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.3}>Due Date</Typography>
                    <Typography variant="body1" fontWeight="700" color="error.main">{currentBillData?.dueDate || "N/A"}</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#f8f9fa", display: "flex", alignItems: "center", gap: 1.5 }}>
                    <CalendarTodayIcon sx={{ color, fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Bill Date</Typography>
                      <Typography variant="body2" fontWeight="600">{currentBillData?.billDate || "N/A"}</Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#f8f9fa", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="body2" fontWeight="600" color="text.secondary">Status</Typography>
                    <Chip
                      label={currentBillData?.approval?.status || "Pending"}
                      color={currentBillData?.approval?.status === "approved" ? "success" : "warning"}
                      size="small" sx={{ fontWeight: 700, textTransform: "capitalize" }}
                    />
                  </Box>
                </Grid>

                {currentBillData?.landlordMessage?.length > 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#fff3e0", border: "1px solid #ff9800" }}>
                      <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
                        <MessageIcon color="warning" sx={{ fontSize: 16 }} />
                        <Typography variant="caption" fontWeight="700">Landlord Message</Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">{currentBillData.landlordMessage.join(", ")}</Typography>
                    </Box>
                  </Grid>
                )}

                {/* Landlord's bill files */}
                {(() => {
                  const raw = currentBillData?.billImages?.landlord;
                  if (!raw) return null;
                  const files = Array.isArray(raw) ? raw
                    : Array.isArray(raw?.images) ? raw.images
                    : raw?.image ? [raw.image]
                    : typeof raw === "string" ? [raw]
                    : [];
                  if (files.length === 0) return null;
                  const uploadedAt = raw?.uploadedAt;
                  return (
                    <Grid item xs={12}>
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#f8f9fa" }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <ImageIcon sx={{ color, fontSize: 16 }} />
                            <Typography variant="caption" fontWeight="700">Landlord's Bill {files.length > 1 ? `Files (${files.length})` : "File"}</Typography>
                          </Stack>
                          {uploadedAt && (
                            <Typography variant="caption" color="text.secondary">{new Date(uploadedAt).toLocaleDateString()}</Typography>
                          )}
                        </Stack>
                        <Stack spacing={1.5}>
                          {files.map((url, idx) => {
                            const isPdf = typeof url === "string" && (url.toLowerCase().includes(".pdf") || url.toLowerCase().includes("application/pdf") || url.toLowerCase().includes("/pdf"));
                            return (
                              <Box key={idx}>
                                {isPdf ? (
                                  <Box sx={{ borderRadius: 2, overflow: "hidden", border: `2px solid ${color}` }}>
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, py: 1, backgroundColor: color }}>
                                      <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <PictureAsPdfIcon sx={{ color: "#fff", fontSize: 18 }} />
                                        <Typography variant="caption" sx={{ color: "#fff", fontWeight: 700 }}>{files.length > 1 ? `PDF ${idx + 1}` : "Bill PDF"}</Typography>
                                      </Stack>
                                      <Stack direction="row" spacing={0.5}>
                                        <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: 13 }} />}
                                          onClick={() => { const a = document.createElement("a"); a.href = url; a.download = `bill-${idx + 1}.pdf`; a.target = "_blank"; a.click(); }}
                                          sx={{ backgroundColor: "rgba(255,255,255,0.22)", color: "#fff", fontWeight: 700, fontSize: "0.68rem", textTransform: "none", borderRadius: 1.5, px: 1, py: 0.3, border: "1px solid rgba(255,255,255,0.35)", "&:hover": { backgroundColor: "rgba(255,255,255,0.35)" }, minWidth: 0 }}>
                                          Download
                                        </Button>
                                        <Button size="small" startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                                          onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                                          sx={{ backgroundColor: "rgba(255,255,255,0.22)", color: "#fff", fontWeight: 700, fontSize: "0.68rem", textTransform: "none", borderRadius: 1.5, px: 1, py: 0.3, border: "1px solid rgba(255,255,255,0.35)", "&:hover": { backgroundColor: "rgba(255,255,255,0.35)" }, minWidth: 0 }}>
                                          Open
                                        </Button>
                                      </Stack>
                                    </Box>
                                    <Box component="iframe" src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} title={`Bill PDF ${idx + 1}`}
                                      sx={{ width: "100%", height: 320, border: "none", display: "block", backgroundColor: "#fff" }} />
                                  </Box>
                                ) : (
                                  <Box sx={{ position: "relative" }}>
                                    <Box
                                      component="img" src={url} alt={`Bill ${idx + 1}`}
                                      onClick={() => window.open(url, "_blank")}
                                      sx={{
                                        width: "100%", maxHeight: 280, borderRadius: 2,
                                        objectFit: "contain", cursor: "pointer", display: "block",
                                        bgcolor: "#f0f0f0", border: `2px solid ${color}`,
                                        boxShadow: "0 4px 15px rgba(0,0,0,0.12)",
                                        transition: "transform 0.2s", "&:hover": { transform: "scale(1.01)" },
                                      }}
                                    />
                                    <Button size="small" startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                                      onClick={() => window.open(url, "_blank")}
                                      sx={{ fontSize: "0.72rem", textTransform: "none", color, fontWeight: 600, p: 0, mt: 0.5 }}>
                                      {files.length > 1 ? `View image ${idx + 1}` : "View full size"}
                                    </Button>
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    </Grid>
                  );
                })()}

                {/* Tenant's verified copy */}
                {(() => {
                  const raw = currentBillData?.billImages?.tenant;
                  if (!raw) return null;
                  const files = Array.isArray(raw) ? raw
                    : Array.isArray(raw?.images) ? raw.images
                    : raw?.image ? [raw.image]
                    : typeof raw === "string" ? [raw]
                    : [];
                  if (files.length === 0) return null;
                  return (
                    <Grid item xs={12}>
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#f0fdf4", border: "1px solid #10B981" }}>
                        <Stack direction="row" alignItems="center" spacing={0.5} mb={1.5}>
                          <VerifiedIcon sx={{ color: "#10B981", fontSize: 16 }} />
                          <Typography variant="caption" fontWeight="700" color="#10B981">
                            Tenant Verified Copy{files.length > 1 ? ` (${files.length} files)` : ""}
                          </Typography>
                        </Stack>
                        <Stack spacing={1.5}>
                          {files.map((url, idx) => {
                            const isPdf = typeof url === "string" && (url.toLowerCase().includes(".pdf") || url.toLowerCase().includes("application/pdf"));
                            return (
                              <Box key={idx}>
                                {isPdf ? (
                                  <Box sx={{ borderRadius: 2, overflow: "hidden", border: "2px solid #10B981" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, py: 1, backgroundColor: "#10B981" }}>
                                      <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <PictureAsPdfIcon sx={{ color: "#fff", fontSize: 18 }} />
                                        <Typography variant="caption" sx={{ color: "#fff", fontWeight: 700 }}>Verified Copy PDF</Typography>
                                      </Stack>
                                      <Stack direction="row" spacing={0.5}>
                                        <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: 13 }} />}
                                          onClick={() => { const a = document.createElement("a"); a.href = url; a.download = `tenant-bill-${idx + 1}.pdf`; a.target = "_blank"; a.click(); }}
                                          sx={{ backgroundColor: "rgba(255,255,255,0.22)", color: "#fff", fontWeight: 700, fontSize: "0.68rem", textTransform: "none", borderRadius: 1.5, px: 1, py: 0.3, border: "1px solid rgba(255,255,255,0.35)", "&:hover": { backgroundColor: "rgba(255,255,255,0.35)" }, minWidth: 0 }}>
                                          Download
                                        </Button>
                                        <Button size="small" startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                                          onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                                          sx={{ backgroundColor: "rgba(255,255,255,0.22)", color: "#fff", fontWeight: 700, fontSize: "0.68rem", textTransform: "none", borderRadius: 1.5, px: 1, py: 0.3, border: "1px solid rgba(255,255,255,0.35)", "&:hover": { backgroundColor: "rgba(255,255,255,0.35)" }, minWidth: 0 }}>
                                          Open
                                        </Button>
                                      </Stack>
                                    </Box>
                                    <Box component="iframe" src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} title={`Tenant Bill PDF ${idx + 1}`}
                                      sx={{ width: "100%", height: 280, border: "none", display: "block", backgroundColor: "#fff" }} />
                                  </Box>
                                ) : (
                                  <Box>
                                    <Box
                                      component="img" src={url} alt={`Tenant Bill ${idx + 1}`}
                                      onClick={() => window.open(url, "_blank")}
                                      sx={{
                                        width: "100%", maxHeight: 240, borderRadius: 2,
                                        objectFit: "contain", cursor: "pointer", display: "block",
                                        bgcolor: "#f0fdf4", border: "2px solid #10B981",
                                        boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
                                        transition: "transform 0.2s", "&:hover": { transform: "scale(1.01)" },
                                      }}
                                    />
                                    <Button size="small" startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                                      onClick={() => window.open(url, "_blank")}
                                      sx={{ fontSize: "0.72rem", textTransform: "none", color: "#10B981", fontWeight: 600, p: 0, mt: 0.5 }}>
                                      View full size
                                    </Button>
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    </Grid>
                  );
                })()}

              </Grid>
            </Box>
          </Collapse>
        )}
      </Card>
    );
  }

  // ── Shared file upload area UI ────────────────────────────────────────────────
  function FileUploadArea({ formikInstance, color, bgColor, locked = false }) {
    return (
      <Box>
        {!imagePreview ? (
          <Paper
            component={locked ? "div" : "label"}
            sx={{
              border: `2px dashed ${formikInstance.touched.bill_image && formikInstance.errors.bill_image ? "#ef4444" : locked ? "#cbd5e1" : "#e2e8f0"}`,
              borderRadius: 3, minHeight: 180,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: locked
                ? "#f1f5f9"
                : `linear-gradient(135deg, ${bgColor || "#f8fafc"} 0%, #fff 100%)`,
              cursor: locked || isSubmitting ? "not-allowed" : "pointer",
              transition: "border-color 0.2s, box-shadow 0.2s",
              ...(!locked && { "&:hover": { borderColor: color, boxShadow: `0 4px 16px -4px ${color}30` } }),
            }}
          >
            <Box sx={{ textAlign: "center", p: 3 }}>
              <CloudUploadIcon sx={{ fontSize: 52, color: locked ? "#94a3b8" : (color || "#667eea"), mb: 1 }} />
              <Typography variant="h6" fontWeight={600} color={locked ? "#94a3b8" : "#1e293b"} mb={0.5}>
                {locked ? "File Locked (Auto-filled)" : "Click to Upload"}
              </Typography>
              <Typography variant="body2" color="#64748b">
                {locked ? "Consumer number fetched this bill" : "JPG, PNG, WEBP, PDF supported"}
              </Typography>
            </Box>
            {!locked && (
              <input
                hidden type="file"
                accept="image/*,application/pdf"
                disabled={isSubmitting}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleFileSelect(file, formikInstance);
                }}
              />
            )}
          </Paper>
        ) : (
          <Box>
            {isPdfFile(previewFile) ? (
              <PdfPreview
                url={imagePreview}
                color={locked ? "#94a3b8" : color}
                onDownload={!locked ? handleDownloadPdf : undefined}
                onOpenNewTab={handleOpenPdfNewTab}
              />
            ) : (
              <Paper sx={{ borderRadius: 3, overflow: "hidden", border: `2px solid ${locked ? "#cbd5e1" : color}`, mb: 2 }}>
                <Box component="img" src={imagePreview} alt="Preview"
                  sx={{ width: "100%", maxHeight: 300, objectFit: "contain", bgcolor: "#f8fafc" }} />
              </Paper>
            )}
            {/* Only show change/remove when not locked */}
            {!locked && (
              <Stack direction="row" spacing={1.5}>
                <Button component="label" fullWidth variant="outlined"
                  startIcon={<CloudUploadIcon />} disabled={isSubmitting}
                  sx={{ borderRadius: 2.5, py: 1.2, textTransform: "none", fontWeight: 600, borderColor: color, color, borderWidth: 2, "&:hover": { borderWidth: 2, backgroundColor: bgColor } }}>
                  Change File
                  <input hidden type="file" accept="image/*,application/pdf"
                    onChange={(e) => { const f = e.target.files[0]; if (f) handleFileSelect(f, formikInstance); }} />
                </Button>
                <Button fullWidth variant="outlined" color="error"
                  startIcon={<DeleteOutlineIcon />} disabled={isSubmitting}
                  onClick={() => handleFileRemove(formikInstance)}
                  sx={{ borderRadius: 2.5, py: 1.2, textTransform: "none", fontWeight: 600, borderWidth: 2, "&:hover": { borderWidth: 2 } }}>
                  Remove
                </Button>
              </Stack>
            )}
            {/* If locked but user can still open/download */}
            {locked && (
              <Stack direction="row" spacing={1.5} justifyContent="center" mt={1}>
                <Button size="small" startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                  onClick={handleOpenPdfNewTab}
                  sx={{ fontSize: "0.78rem", textTransform: "none", color: "#64748b", fontWeight: 600 }}>
                  Open in new tab
                </Button>
              </Stack>
            )}
          </Box>
        )}
        {formikInstance.touched.bill_image && formikInstance.errors.bill_image && (
          <Typography color="error" variant="caption" display="block" mt={1}>
            {formikInstance.errors.bill_image}
          </Typography>
        )}
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Original TransitionsModal — unchanged */}
      <TransitionsModal
        modalValue={isModalVisible}           setIsModalVisible={setIsModalVisible}
        ssgcModal={ssgcModal}                 setSsgcModal={setSsgcModal}
        waterModal={waterModal}               setWaterModal={setWaterModal}
        maintainanceModal={maintainanceModal} setMaintainanceModal={setMaintainanceModal}
        trashModal={trashModal}               setTrashModal={setTrashModal}
        selectedBillData={selectedBillData}
        selectedBillType={selectedBillType}
        landlordId={billData?.landlordId || billData?.landlord_id}
        flatId={billData?.flatId || billData?.flat_id || getId?.split("/")?.[1]}
      />

      <Wrapper />

      {/* Full-screen: page loading */}
      {isFetching && (
        <Box sx={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)" }}>
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={64} thickness={4} sx={{ color: "#667eea" }} />
            <Typography sx={{ mt: 2, color: "#64748b", fontWeight: 600, fontSize: "1.1rem" }}>Loading bills…</Typography>
          </Box>
        </Box>
      )}

      {/* Full-screen: uploading */}
      <Backdrop open={isSubmitting} sx={{ zIndex: 9999, backgroundColor: "rgba(10,18,35,0.78)", backdropFilter: "blur(8px)" }}>
        <Box sx={{ textAlign: "center" }}>
          <Box sx={{ position: "relative", display: "inline-flex", mb: 3 }}>
            <CircularProgress size={84} thickness={2.8} sx={{ color: activeCard?.color || "#667eea" }} />
            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2rem" }}>
              {activeCard?.icon || "📄"}
            </Box>
          </Box>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.2rem" }}>
            {modalOpen === "verify" ? "Submitting Verification…" : `Uploading ${activeCard?.title} Bill…`}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.875rem", mt: 0.5 }}>
            Please wait, do not close this window
          </Typography>
        </Box>
      </Backdrop>

      {/* Full-screen: auto-fill fetching */}
      <Backdrop open={autoFillLoading} sx={{ zIndex: 9998, backgroundColor: "rgba(10,18,35,0.78)", backdropFilter: "blur(8px)" }}>
        <Box sx={{ textAlign: "center" }}>
          <Box sx={{ position: "relative", display: "inline-flex", mb: 3 }}>
            <CircularProgress size={84} thickness={2.8} sx={{ color: activeCard?.color || "#FFB020" }} />
            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2rem" }}>
              {activeCard?.icon || "⚡"}
            </Box>
          </Box>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.2rem" }}>Fetching Bill Data…</Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.875rem", mt: 0.5 }}>Connecting to {activeCard?.company}</Typography>
        </Box>
      </Backdrop>

      {/* Page Shell */}
      <Box
        className={`${open ? "sidebar-open" : "sidebar-closed"}`}
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          pt: 5, pb: 8, position: "relative",
          "&::before": { content: '""', position: "absolute", inset: 0, pointerEvents: "none", background: "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)" },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ mb: 5, textAlign: "center" }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: "#fff", mb: 1.5, textShadow: "0 2px 20px rgba(0,0,0,0.2)", letterSpacing: "-0.5px" }}>
              Current Bills
            </Typography>
            <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.88)", fontWeight: 400, maxWidth: 560, mx: "auto" }}>
              {isTenantUploader ? "Upload and manage your utility bills" : "View and pay your utility bills"}
            </Typography>
            {isTenantUploader && (
              <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 3, maxWidth: 580, mx: "auto", borderRadius: 3, bgcolor: "rgba(255,255,255,0.95)", backdropFilter: "blur(10px)", "& .MuiAlert-icon": { color: "#0EA5E9" } }}>
                <AlertTitle sx={{ fontWeight: 700, color: "#0f172a" }}>Upload Mode Active</AlertTitle>
                <Typography variant="body2" sx={{ color: "#475569" }}>You have permission to upload bills. Click any card to get started.</Typography>
              </Alert>
            )}
          </Box>

          {!isFetching && !isTenantUploader && (
            <PreviousUnpaidBills
              items={previousUnpaid}
              total={previousTotal}
              onPay={handlePayClick}
              onPayOnline={startPayment}
              payingId={payingId}
            />
          )}

          {!isFetching && !isTenantUploader && !cycle?.awaitingFirstCycle && (
            <MonthlyBillSummary
              flatDetail={matchedFlat?.flatDetail}
              billData={billData}
              dueTypes={dueBillTypes}
              cycle={cycle}
              onPayOnline={startPayment}
              payingId={payingId}
            />
          )}

          {/* Cards */}
          {!isFetching && !isTenantUploader && bills.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8, bgcolor: "rgba(255,255,255,0.12)", borderRadius: 4, border: "2px dashed rgba(255,255,255,0.4)" }}>
              {/* A brand-new tenancy has no bill because its first cycle has
                  not finished yet — that is a schedule, not an absent owner.
                  Saying which is the difference between a calm tenant and a
                  support call. */}
              {cycle?.awaitingFirstCycle ? (
                <>
                  <Typography variant="h2" mb={2}>🗓️</Typography>
                  <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700, mb: 1 }}>
                    Your first bill will arrive on {cycle.firstBillOn}
                  </Typography>
                  <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.85)", maxWidth: 520, mx: "auto" }}>
                    {cycle.emptyStateMessage}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mt: 1.5 }}>
                    Each month's billing cycle runs from the 1st to the 1st.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h2" mb={2}>📭</Typography>
                  <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700, mb: 1 }}>
                    {cycle?.cycleLabel ? `${cycle.cycleLabel} bill hasn't arrived yet` : "No Bills Yet"}
                  </Typography>
                  <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.8)", maxWidth: 520, mx: "auto" }}>
                    {cycle?.isRaised
                      ? `This cycle's bill is generated on ${cycle.raisedOn}. Your landlord hasn't submitted a figure yet.`
                      : "Your landlord hasn't uploaded any bills yet. Check back later."}
                  </Typography>
                </>
              )}
            </Box>
          ) : (
            <Grid container spacing={3} justifyContent="center">
              {bills.map((bill, index) => (
                <Grid item xs={12} sm={6} md={4} key={bill.type}>
                  <Fade in timeout={400 + index * 100}>
                    <div><BillCard billType={bill.type} cardMeta={bill} /></div>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════════════════════════
          UPLOAD MODAL
          Used for:
          A. isTenantUploader === true  → tenant uploads their own bill
          B. isTenantUploader === false → owner forgot, tenant self-submits
             via consumer number (electric/gas only, auto-fill enabled)
      ══════════════════════════════════════════════════════════════════════ */}
      <Modal open={modalOpen === "upload"} onClose={handleCloseModal} closeAfterTransition>
        <Fade in={modalOpen === "upload"}>
          <Box sx={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 660 }, maxHeight: "90vh", overflowY: "auto",
            bgcolor: "#fff", borderRadius: 5, boxShadow: "0 25px 80px rgba(0,0,0,0.3)", outline: "none",
          }}>
            {/* Modal Header */}
            <Box sx={{
              background: activeCard ? `linear-gradient(135deg, ${activeCard.color} 0%, ${activeCard.color}cc 100%)` : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              p: 4, position: "relative", color: "#fff", borderRadius: "20px 20px 0 0",
            }}>
              <IconButton onClick={handleCloseModal} disabled={isSubmitting}
                sx={{ position: "absolute", right: 16, top: 16, color: "#fff", bgcolor: "rgba(255,255,255,0.2)", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}>
                <CloseIcon />
              </IconButton>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ width: 64, height: 64, fontSize: "2rem", bgcolor: "rgba(255,255,255,0.2)", border: "3px solid rgba(255,255,255,0.3)" }}>
                  {activeCard?.icon}
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700} mb={0.5}>
                    {isTenantUploader ? `Upload ${activeCard?.title} Bill` : `Submit ${activeCard?.title} Bill`}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {isTenantUploader
                      ? (showAutoFill ? "Auto-fill with consumer number, or fill manually" : "Fill in the details and attach your bill")
                      : "Enter your consumer number to fetch and submit the bill"
                    }
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* ── Consumer Number + Auto-Fill section ────────────────────── */}
            {/* Show for:
                - isTenantUploader===true with electric/gas
                - isTenantUploader===false with electric/gas (tenant self-submit) */}
            {showAutoFill && (
              <Box sx={{ px: 4, pt: 3, pb: 0 }}>
                <Box sx={{
                  p: 2.5, borderRadius: 3,
                  backgroundColor: billFetched ? `${activeCard?.color}08` : "#f8fafc",
                  border: `2px solid ${
                    consumerNumberError ? "#ef4444"
                    : billFetched ? activeCard?.color
                    : consumerNumber.length === consumerNumLength ? activeCard?.color
                    : "#e2e8f0"
                  }`,
                  transition: "all 0.25s ease",
                  ...(!billFetched && { "&:focus-within": { borderColor: activeCard?.color, backgroundColor: "#fff", boxShadow: `0 0 0 3px ${activeCard?.color}20` } }),
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TagIcon sx={{ fontSize: 20, color: activeCard?.color }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.875rem" }}>Consumer Number</Typography>
                      <Chip label="Optional" size="small" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, backgroundColor: "#f1f5f9", color: "#64748b", "& .MuiChip-label": { px: 1 } }} />
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.72rem", color: consumerNumber.length === consumerNumLength ? activeCard?.color : "#cbd5e1" }}>
                      {consumerNumber.length}/{consumerNumLength}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <TextField
                      fullWidth size="small"
                      placeholder={`Enter ${consumerNumLength}-digit number`}
                      value={consumerNumber}
                      onChange={handleConsumerNumberChange}
                      inputProps={{ maxLength: consumerNumLength, inputMode: "numeric" }}
                      error={!!consumerNumberError}
                      helperText={consumerNumberError || " "}
                      FormHelperTextProps={{ sx: { mt: 0.5, fontSize: "0.72rem" } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <TagIcon sx={{ fontSize: 18, color: consumerNumber.length === consumerNumLength ? activeCard?.color : "#94a3b8" }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff", borderRadius: 2, fontWeight: 600, letterSpacing: consumerNumber.length > 0 ? "0.08em" : "normal", "& fieldset": { borderColor: "transparent" }, "&:hover fieldset": { borderColor: "transparent" }, "&.Mui-focused fieldset": { borderColor: "transparent" }, "&.Mui-error fieldset": { borderColor: "transparent" } } }}
                    />
                    <Button
                      variant="contained"
                      startIcon={autoFillLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <AutoFixHighIcon />}
                      onClick={handleAutoFill}
                      disabled={!isAutoFillEnabled || billFetched}
                      sx={{
                        whiteSpace: "nowrap", flexShrink: 0, borderRadius: 2.5, px: 2.5, py: 1, mb: "22px",
                        fontWeight: 700, textTransform: "none", fontSize: "0.85rem",
                        backgroundColor: (isAutoFillEnabled && !billFetched) ? activeCard?.color : "#e2e8f0",
                        color: (isAutoFillEnabled && !billFetched) ? "#fff" : "#94a3b8",
                        boxShadow: (isAutoFillEnabled && !billFetched) ? `0 4px 14px ${activeCard?.color}50` : "none",
                        "&:hover": { opacity: 0.88 },
                        "&:disabled": { backgroundColor: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
                      }}
                    >
                      {autoFillLoading ? "Fetching…" : billFetched ? "Fetched ✓" : "Auto-Fill"}
                    </Button>
                  </Box>

                  {/* Info row */}
                  {!billFetched ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <InfoOutlinedIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                      <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.72rem" }}>
                        Enter consumer number to auto-fill bill amount and attach PDF, or fill manually below
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, p: 1, borderRadius: 1.5, bgcolor: `${activeCard?.color}10` }}>
                      <VerifiedIcon sx={{ fontSize: 14, color: activeCard?.color }} />
                      <Typography variant="caption" sx={{ color: activeCard?.color, fontSize: "0.72rem", fontWeight: 700 }}>
                        Bill fetched — amount and file are auto-set. You can still edit the message.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Upload Form */}
            <form onSubmit={uploadFormik.handleSubmit}>
              <Box sx={{ p: 4 }}>
                <Grid container spacing={3}>

                  {/* Total Amount — locked when auto-filled */}
                  <Grid item xs={12}>
                    <Box sx={{
                      p: 2.5, borderRadius: 3,
                      backgroundColor: billFetched ? "#f1f5f9" : "#f8fafc",
                      border: `2px solid ${billFetched ? "#cbd5e1" : "#e2e8f0"}`,
                      opacity: billFetched ? 0.72 : 1,
                      transition: "all 0.2s",
                      ...(!billFetched && { "&:focus-within": { borderColor: activeCard?.color, bgcolor: "#fff", boxShadow: `0 0 0 3px ${activeCard?.color}20` } }),
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <AttachMoneyIcon sx={{ fontSize: 20, color: billFetched ? "#94a3b8" : activeCard?.color }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: billFetched ? "#64748b" : "#0f172a", fontSize: "0.875rem" }}>
                          Total Amount *
                        </Typography>
                        {billFetched && (
                          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
                            <LockIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                            <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, fontSize: "0.7rem" }}>Auto-set</Typography>
                          </Box>
                        )}
                      </Box>
                      <TextField
                        fullWidth size="small" placeholder="Enter bill amount" name="total_amount"
                        value={uploadFormik.values.total_amount}
                        onChange={uploadFormik.handleChange}
                        onBlur={uploadFormik.handleBlur}
                        InputProps={{ readOnly: billFetched }}
                        disabled={isSubmitting}
                        error={uploadFormik.touched.total_amount && Boolean(uploadFormik.errors.total_amount)}
                        helperText={uploadFormik.touched.total_amount && uploadFormik.errors.total_amount}
                        sx={{ "& .MuiOutlinedInput-root": { backgroundColor: billFetched ? "#e9eef4" : "#fff", borderRadius: 2, cursor: billFetched ? "not-allowed" : "text", "& fieldset": { borderColor: "transparent" }, "&:hover fieldset": { borderColor: "transparent" }, "&.Mui-focused fieldset": { borderColor: "transparent" } } }}
                      />
                    </Box>
                  </Grid>

                  {/* Message — always editable */}
                  <Grid item xs={12}>
                    <FieldBox color={activeCard?.color} bgColor={activeCard?.bgColor}>
                      <FieldLabel icon={<MessageIcon sx={{ fontSize: 20, color: activeCard?.color }} />} label="Message (Optional)" />
                      <TextField
                        fullWidth size="small" multiline rows={3}
                        placeholder={isTenantUploader ? "Add a note for the landlord..." : "Add a note..."}
                        name="message"
                        value={uploadFormik.values.message}
                        onChange={uploadFormik.handleChange}
                        onBlur={uploadFormik.handleBlur}
                        disabled={isSubmitting}
                        error={uploadFormik.touched.message && Boolean(uploadFormik.errors.message)}
                        helperText={uploadFormik.touched.message && uploadFormik.errors.message}
                        sx={cleanInputSx}
                      />
                    </FieldBox>
                  </Grid>

                  {/* File Upload — locked when auto-filled */}
                  <Grid item xs={12}>
                    <FieldBox color={billFetched ? "#94a3b8" : activeCard?.color} bgColor={activeCard?.bgColor}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <AddPhotoAlternateIcon sx={{ fontSize: 20, color: billFetched ? "#94a3b8" : activeCard?.color }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: billFetched ? "#64748b" : "#0f172a", fontSize: "0.875rem" }}>
                          Bill Image / PDF *
                        </Typography>
                        {billFetched && (
                          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
                            <LockIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                            <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, fontSize: "0.7rem" }}>Auto-attached</Typography>
                          </Box>
                        )}
                      </Box>
                      <FileUploadArea
                        formikInstance={uploadFormik}
                        color={activeCard?.color}
                        bgColor={activeCard?.bgColor}
                        locked={billFetched}
                      />
                    </FieldBox>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />

                <Stack direction="row" spacing={2}>
                  <Button fullWidth variant="outlined" onClick={handleCloseModal} disabled={isSubmitting}
                    sx={{ borderRadius: 3, py: 1.8, textTransform: "none", fontWeight: 700, fontSize: "1rem", color: "#64748b", borderColor: "#e2e8f0", borderWidth: 2, "&:hover": { borderWidth: 2, borderColor: "#cbd5e1", bgcolor: "#f8fafc" }, "&:disabled": { borderColor: "#e2e8f0", color: "#cbd5e1" } }}>
                    Cancel
                  </Button>
                  <Button fullWidth type="submit" variant="contained" disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <CloudUploadIcon />}
                    sx={{ borderRadius: 3, py: 1.8, textTransform: "none", fontWeight: 700, fontSize: "1rem", background: activeCard ? `linear-gradient(135deg, ${activeCard.color} 0%, ${activeCard.color}cc 100%)` : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", boxShadow: `0 8px 24px ${activeCard?.color || "#667eea"}50`, "&:hover": { opacity: 0.92 }, "&:disabled": { background: "#cbd5e1", color: "#fff", boxShadow: "none" } }}>
                    {isSubmitting ? "Uploading…" : isTenantUploader ? "Upload Bill" : "Submit Bill"}
                  </Button>
                </Stack>
              </Box>
            </form>
          </Box>
        </Fade>
      </Modal>

      {/* ══════════════════════════════════════════════════════════════════════
          VERIFY MODAL
          Used when isTenantUploader === false AND owner HAS already uploaded
          Tenant uploads their own physical copy to verify the owner's bill
          Amount is shown read-only from owner's data — no editable amount field
      ══════════════════════════════════════════════════════════════════════ */}
      <Modal open={modalOpen === "verify"} onClose={handleCloseModal} closeAfterTransition>
        <Fade in={modalOpen === "verify"}>
          <Box sx={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 560 }, maxHeight: "90vh", overflowY: "auto",
            bgcolor: "#fff", borderRadius: 5, boxShadow: "0 25px 80px rgba(0,0,0,0.3)", outline: "none",
          }}>
            {/* Header */}
            <Box sx={{
              background: activeCard ? `linear-gradient(135deg, ${activeCard.color} 0%, ${activeCard.color}cc 100%)` : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              p: 4, position: "relative", color: "#fff", borderRadius: "20px 20px 0 0",
            }}>
              <IconButton onClick={handleCloseModal} disabled={isSubmitting}
                sx={{ position: "absolute", right: 16, top: 16, color: "#fff", bgcolor: "rgba(255,255,255,0.2)", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}>
                <CloseIcon />
              </IconButton>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ width: 64, height: 64, fontSize: "2rem", bgcolor: "rgba(255,255,255,0.2)", border: "3px solid rgba(255,255,255,0.3)" }}>
                  {activeCard?.icon}
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700} mb={0.5}>Verify {activeCard?.title} Bill</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Upload your physical copy to confirm the bill</Typography>
                </Box>
              </Stack>
            </Box>

            <form onSubmit={verifyFormik.handleSubmit}>
              <Box sx={{ p: 4 }}>
                {/* Info alert */}
                <Alert severity="info" sx={{ mb: 3, borderRadius: 3, border: `1px solid ${activeCard?.color}40`, backgroundColor: activeCard?.bgColor, "& .MuiAlert-icon": { color: activeCard?.color } }}>
                  <Typography variant="body2" sx={{ color: "#475569" }}>
                    Your landlord has uploaded this bill. Upload your physical copy to verify the amount charged.
                  </Typography>
                </Alert>

                {/* Show landlord's bill amount (read-only) */}
                {activeType && billData && (() => {
                  const d = getBillDataByType(billData, activeType);
                  if (!d?.billAmount) return null;
                  return (
                    <Box sx={{
                      mb: 3, p: 2, borderRadius: 3, bgcolor: "#f1f5f9",
                      border: "2px solid #cbd5e1", display: "flex", alignItems: "center", gap: 1.5,
                    }}>
                      <LockIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, fontSize: "0.72rem" }}>
                          Landlord's Bill Amount (read-only)
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 800, color: "#0f172a" }}>
                          Rs. {d.billAmount}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })()}

                <FieldBox color={activeCard?.color} bgColor={activeCard?.bgColor}>
                  <FieldLabel icon={<AddPhotoAlternateIcon sx={{ fontSize: 20, color: activeCard?.color }} />} label="Your Bill Copy *" />
                  <FileUploadArea formikInstance={verifyFormik} color={activeCard?.color} bgColor={activeCard?.bgColor} locked={false} />
                </FieldBox>

                <Divider sx={{ my: 4 }} />

                <Stack direction="row" spacing={2}>
                  <Button fullWidth variant="outlined" onClick={handleCloseModal} disabled={isSubmitting}
                    sx={{ borderRadius: 3, py: 1.8, textTransform: "none", fontWeight: 700, fontSize: "1rem", color: "#64748b", borderColor: "#e2e8f0", borderWidth: 2, "&:hover": { borderWidth: 2, borderColor: "#cbd5e1", bgcolor: "#f8fafc" }, "&:disabled": { borderColor: "#e2e8f0", color: "#cbd5e1" } }}>
                    Cancel
                  </Button>
                  <Button fullWidth type="submit" variant="contained" disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <VerifiedIcon />}
                    sx={{ borderRadius: 3, py: 1.8, textTransform: "none", fontWeight: 700, fontSize: "1rem", background: activeCard ? `linear-gradient(135deg, ${activeCard.color} 0%, ${activeCard.color}cc 100%)` : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", boxShadow: `0 8px 24px ${activeCard?.color || "#667eea"}50`, "&:hover": { opacity: 0.92 }, "&:disabled": { background: "#cbd5e1", color: "#fff", boxShadow: "none" } }}>
                    {isSubmitting ? "Submitting…" : "Submit Verification"}
                  </Button>
                </Stack>
              </Box>
            </form>
          </Box>
        </Fade>
      </Modal>

      {onlinePayDialog}
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover theme="light" style={{ zIndex: 99999 }} />
    </>
  );
}

// ── Shared UI helpers ─────────────────────────────────────────────────────────
function FieldBox({ color, bgColor, children }) {
  return (
    <Box sx={{
      p: 2.5, borderRadius: 3, bgcolor: "#f8fafc",
      border: "2px solid #e2e8f0", transition: "all 0.2s",
      "&:focus-within": {
        borderColor: color || "#667eea", bgcolor: "#fff",
        boxShadow: `0 0 0 3px ${color || "#667eea"}20`,
      },
    }}>
      {children}
    </Box>
  );
}

function FieldLabel({ icon, label }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
      {icon}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.875rem" }}>
        {label}
      </Typography>
    </Stack>
  );
}

const cleanInputSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#fff", borderRadius: 2,
    "& fieldset": { borderColor: "transparent" },
    "&:hover fieldset": { borderColor: "transparent" },
    "&.Mui-focused fieldset": { borderColor: "transparent" },
  },
};